import { Curve3, Mesh, Ray, TransformNode, Vector3 } from '@babylonjs/core';
import { AssetObject } from '../AssetObject';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { Level } from '../Level';
import { AssetObjectConfig, MeshFactory } from '../MeshFactory';
import { PlayerCollision } from '../PlayerCollision';
import { ENEMY_ANIMATIONS, EnemyBehavior } from './EnemyBehavior';

/**
 * Bomb interface for tracking active bombs
 */
export interface Bomb {
  root: TransformNode;
  mesh: Mesh;
  velocity: Vector3;
}

/**
 * Floating Bomber behavior - floats above path on a bezier curve and drops bombs
 */

export class EnemyFloatingBomberBehavior extends EnemyBehavior {
  private floatPath: Vector3[] = [];
  private pathProgress: number = 0;
  private bombTimer: number = 0;
  private bombs: Bomb[] = [];
  private gravity: Vector3 = new Vector3(0, -15, 0);
  private initialized: boolean = false;

  constructor(startPosition: Vector3, config: EnemyDistributionConfig) {
    super('enemyFloatingBomber', startPosition, config);
  }

  initLevel(level: Level): EnemyFloatingBomberBehavior {
    super.initLevel(level);
    this.level.game.sound.spatial('enemyprojectilehit', this.mesh).then((sound) => {
      // Sound is now registered and can be played via play('enemyprojectilehit', this.mesh)
    });
    return this;
  }

  /**
   * Initialize the floating path when behavior starts
   */
  private initFloatPath(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Generate control points around the start position for a closed bezier loop
    const center = this.startPosition.add(new Vector3(0, this.config.floatHeight, 0));
    const controlPoints: Vector3[] = [];
    const numPoints = 6;

    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      const x = center.x + Math.cos(angle) * this.config.floatRadius;
      const z = center.z + Math.sin(angle) * this.config.floatRadius;
      // Add slight Y variation for more organic movement
      const yVariation = Math.sin(angle * 2) * (this.config.floatRadius * 0.3);
      controlPoints.push(new Vector3(x, center.y + yVariation, z));
    }

    // Create smooth closed Catmull-Rom spline
    const spline = Curve3.CreateCatmullRomSpline(controlPoints, 20, true);
    this.floatPath = spline.getPoints();

    // Start at random position on path
    if (this.level) {
      this.pathProgress = this.level.rng.uFloat32() * this.floatPath.length;
    }
  }

  behaviorUpdate(deltaTime: number): void {
    // Initialize path on first update
    this.initFloatPath();

    // Play idle animation (hovering)
    this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);

    // Move along float path
    if (this.floatPath.length > 0) {
      this.pathProgress += this.config.floatSpeed * deltaTime * 10; // Multiply for smoother movement
      if (this.pathProgress >= this.floatPath.length) {
        this.pathProgress = 0;
      }

      // Interpolate position on path
      const pathIndex = Math.floor(this.pathProgress);
      const nextIndex = (pathIndex + 1) % this.floatPath.length;
      const t = this.pathProgress - pathIndex;

      const currentPoint = this.floatPath[pathIndex];
      const nextPoint = this.floatPath[nextIndex];

      if (currentPoint && nextPoint) {
        const newPosition = Vector3.Lerp(currentPoint, nextPoint, t);
        this.mesh.position.copyFrom(newPosition);
      }

      // Look at center
      const center = this.startPosition.add(new Vector3(0, this.config.floatHeight, 0));
      this.lookAt(center);
    }

    // Update bomb timer and spawn bombs
    this.bombTimer += deltaTime;
    if (this.bombTimer >= this.config.bombInterval) {
      this.bombTimer = 0;
      this.spawnBomb();
    }

    // Update active bombs
    this.updateBombs(deltaTime);
  }

  /**
   * Spawn a bomb at the current enemy position
   */
  private spawnBomb(): void {
    if (!this.level) return;

    // Create bomb mesh
    const bombAsset = new AssetObject('bomb').withClone(true);
    const config = AssetObjectConfig.builder().object(bombAsset).scale(this.config.bombScale).physicsShapeType(undefined).build();
    const bombMesh = config.buildMesh(this.level);
    if (!bombMesh) return;

    MeshFactory.configureMesh(bombMesh, config, this.level);

    // Create root transform for bomb
    const root = new TransformNode('bomb_root', this.level.scene);
    root.position.copyFrom(this.mesh.position);
    bombMesh.parent = root;
    bombMesh.position = Vector3.Zero();

    // Setup player collision for the bomb
    bombMesh.metadata = {
      ...bombMesh.metadata,
      playerCollision: new PlayerCollision(false, (level, player, event) => {
        if (!player.vulnerable || level.game.config.godMode || level.finished) {
          // Player is invincible, just destroy bomb
          this.destroyBomb({ root, mesh: bombMesh, velocity: Vector3.Zero() });
        } else {
          player.collide(event, 'Hit by bomb!');
          this.destroyBomb({ root, mesh: bombMesh, velocity: Vector3.Zero() });
        }
      }),
    };

    // Register collision mesh
    this.level.player?.registerCollisionMesh(bombMesh);

    // Add to active bombs with initial downward velocity
    this.bombs.push({
      root,
      mesh: bombMesh,
      velocity: new Vector3(0, 0, 0), // Starts falling from rest
    });
  }

  /**
   * Update all active bombs - apply gravity and check for ground collision
   */
  private updateBombs(deltaTime: number): void {
    if (!this.level) return;

    for (let i = this.bombs.length - 1; i >= 0; i--) {
      const bomb = this.bombs[i];

      // Apply gravity
      bomb.velocity.addInPlace(this.gravity.scale(deltaTime));

      // Calculate step
      const step = bomb.velocity.scale(deltaTime);
      const newPosition = bomb.root.position.add(step);

      // Check for ground collision via raycast
      const ray = new Ray(bomb.root.position, Vector3.Down(), Math.abs(step.y) + 0.5);
      const hit = this.level.scene.pickWithRay(ray, (mesh) => {
        return mesh.metadata?.ground || mesh.metadata?.way || mesh.metadata?.platform;
      });

      if (hit && hit.hit && hit.pickedPoint && newPosition.y <= hit.pickedPoint.y) {
        // Hit ground - destroy bomb with particles
        this.destroyBomb(bomb);
        this.bombs.splice(i, 1);
        continue;
      }

      // Check if below killing height
      if (newPosition.y < this.level.config.killingHeight) {
        this.destroyBomb(bomb);
        this.bombs.splice(i, 1);
        continue;
      }

      // Update position
      bomb.root.position.copyFrom(newPosition);

      // Rotate bomb for visual effect
      bomb.mesh.rotation.x += 3 * deltaTime;
      bomb.mesh.rotation.z += 2 * deltaTime;
    }
  }

  /**
   * Destroy a bomb with particle effect
   */
  private destroyBomb(bomb: Bomb): void {
    if (!this.level) return;

    // Unregister collision
    this.level.player?.unregisterCollisionMesh(bomb.mesh);

    // Create particle effect
    this.level.particles.createDisappearanceParticleSystem(bomb.mesh, 0.4);

    // Play sound
    this.level.game.sound.play('enemyprojectilehit', this.mesh);

    // Dispose
    bomb.mesh.dispose();
    bomb.root.dispose();
  }

  /**
   * Cleanup all bombs on detach
   */
  detach(): void {
    for (const bomb of this.bombs) {
      this.destroyBomb(bomb);
    }
    this.bombs = [];
    super.detach();
  }

  clone(): EnemyFloatingBomberBehavior {
    return new EnemyFloatingBomberBehavior(this.startPosition, this.config);
  }
}
